import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axiosInstance, {
  CheckUserExpiry,
  getStaffId,
  getUserDetails,
  isCompanyManagerUser,
  isFisherOwnerUser,
  isFisherWideUser,
  isIndependentScientistUser,
  isSuperUserSession,
  mergeUserDetails,
  handleLegacySessionExpired
} from "../../components/content/axiosConfig";
import Swal from "sweetalert2";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import AssignScientistPondsModal from "./AssignScientistPondsModal.jsx";
import { getTeamListRoleDisplay, isLinkedScientistStaff, teamListRoleKey } from "../../utils/teamRoles";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import { resolveScopedFisherID } from "../../utils/pondListForUser";
import { getScopedAccount } from "../../utils/accountContext";

function memberInitial(name) {
  const s = String(name || "").trim();
  return s ? s.charAt(0).toUpperCase() : "T";
}

function roleTone(key) {
  if (key === "owner" || key === "pond_manager") return "natural";
  if (key === "scientist") return "stocked";
  if (key === "angler") return "river";
  return "other";
}

function isOwnerRow(row) {
  return row.IsFisherOwner === true || row.IsFisherOwner === 1 || row.IsFisherOwner === "1";
}

function isLinkedScientistRow(row) {
  return isLinkedScientistStaff(row);
}

function isScientistPrimaryRow(row) {
  return isLinkedScientistStaff(row);
}

/** Org pond-team members and linked scientists can be assigned ponds from the team list. */
function canAssignPondsFromTeamList(row) {
  if (isOwnerRow(row)) return false;
  if (row.IsAdmin === true || row.IsAdmin === 1 || row.IsAdmin === "1") return false;
  return true;
}

/** Owners (and any member) cannot remove their own account from the team list. */
function canDeleteStaffMember(row, currentUser) {
  if (!row?.id) return false;
  const myId = getStaffId(currentUser);
  if (myId != null && String(row.id) === String(myId)) return false;
  return true;
}

function StaffStatusBadge({ staff, canManageTeam, onToggleStatus }) {
  const label = staff.Status ? "Active" : "Pending";
  const tone = staff.Status ? "natural" : "river";
  const body = (
    <span className={`donezo-status-dot donezo-status-dot--${tone}`}>
      <span className="donezo-status-dot__mark" aria-hidden />
      {label}
    </span>
  );
  if (!canManageTeam) return body;
  return (
    <button
      type="button"
      className="donezo-team-status-btn"
      title="Click to toggle Active / Pending"
      onClick={(e) => {
        e.stopPropagation();
        onToggleStatus(staff.id);
      }}
    >
      {body}
    </button>
  );
}

const Staff = () => {
  const history = useHistory();
  const [staffs, setStaffs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [assignPondsStaff, setAssignPondsStaff] = useState(null);
  const [assignPondsMode, setAssignPondsMode] = useState("scientist");
  const userDetails = getUserDetails();
  const isCompanyManager = isCompanyManagerUser(userDetails);
  const isSuperScoped = isSuperUserSession(userDetails) && !!getScopedAccount();
  const scopedAccount = getScopedAccount();
  const companyNeedsScopedAccount = isCompanyManager && !scopedAccount && !isSuperScoped;
  const fisherID = companyNeedsScopedAccount ? null : resolveScopedFisherID(userDetails);
  const myStaffRow = staffs.find((s) => String(s.id) === String(userDetails?.id));
  const canManageTeam =
    isCompanyManager ||
    isSuperScoped ||
    isFisherOwnerUser(userDetails) ||
    !!(myStaffRow && (myStaffRow.IsFisherOwner === true || myStaffRow.IsFisherOwner === 1));
  const canOpenTeamPage =
    isCompanyManager ||
    isSuperScoped ||
    isFisherWideUser(userDetails);

  const staffRoleBadge = (row) => {
    const key = teamListRoleKey(row);
    const hasDefaultRole = !!(row.DefaultPondRole || row.defaultPondRole);
    const hasPondKinds = Array.isArray(row.pondRoleKinds) && row.pondRoleKinds.length > 0;
    if (
      key === "pond_staff" &&
      !hasDefaultRole &&
      !hasPondKinds &&
      !isOwnerRow(row) &&
      !isScientistPrimaryRow(row)
    ) {
      return (
        <span className="donezo-status-dot donezo-status-dot--other">
          <span className="donezo-status-dot__mark" aria-hidden />
          Pond team
        </span>
      );
    }
    const display = getTeamListRoleDisplay(row);
    return (
      <span className={`donezo-status-dot donezo-status-dot--${roleTone(key)}`}>
        <span className="donezo-status-dot__mark" aria-hidden />
        {display.title}
      </span>
    );
  };

  const CheckExpiry = useCallback(async () => {
    await CheckUserExpiry();
  }, []);

  const applyStaffResponse = useCallback((payload) => {
    const users = Array.isArray(payload) ? payload : payload?.data || [];
    const total = Array.isArray(payload) ? payload.length : Number(payload?.total) || 0;
    setStaffs(users);
    setTotalCount(total);
    const cur = getUserDetails();
    const me = users.find((s) => String(s.id) === String(cur?.id));
    if (me && (me.IsFisherOwner === true || me.IsFisherOwner === 1)) {
      mergeUserDetails({ IsFisherOwner: true });
    }
  }, []);

  const runStaffFetch = useCallback(
    async ({ withLoader }) => {
      if (fisherID == null || fisherID === "" || Number.isNaN(Number(fisherID))) {
        return;
      }
      if (withLoader) setIsLoading(true);
      try {
        const response = await axiosInstance.get(`api/User/GetAllStaff/${fisherID}`, {
          params: {
            search: searchTermRef.current,
            page: currentPageRef.current,
            limit: entriesPerPage,
          },
        });
        applyStaffResponse(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
          const errorMessage = error.response?.data?.message || "Failed to fetch staff";
          showErrorToast(errorMessage);
        }
        console.error("Error fetching staff:", error);
      } finally {
        if (withLoader) setIsLoading(false);
      }
    },
    [fisherID, applyStaffResponse, entriesPerPage]
  );

  const refetchStaffQuiet = useCallback(async () => {
    if (fisherID == null || fisherID === "" || Number.isNaN(Number(fisherID))) {
      return;
    }
    try {
      const response = await axiosInstance.get(`api/User/GetAllStaff/${fisherID}`, {
        params: {
          search: searchTermRef.current,
          page: currentPageRef.current,
          limit: entriesPerPage,
        },
      });
      applyStaffResponse(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to refresh team list";
        showErrorToast(errorMessage);
      }
      console.error("Error refreshing staff:", error);
    }
  }, [fisherID, applyStaffResponse, entriesPerPage]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (fisherID || isIndependentScientistUser(getUserDetails())) {
      CheckExpiry();
    }
  }, [fisherID, CheckExpiry]);

  useEffect(() => {
    if (!fisherID) return;
    const delay = searchTerm ? 800 : 0;
    const debounceTimer = setTimeout(() => {
      runStaffFetch({ withLoader: true });
    }, delay);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, currentPage, fisherID, runStaffFetch]);

  const updateStatus = async (memberId) => {
    try {
      await axiosInstance.post(`api/User/ChangeStatus/${memberId}`);
      await refetchStaffQuiet();
      showSuccessToast("Team member status updated.");
    } catch (error) {
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to update member status";
        showErrorToast(errorMessage);
      }
      console.error("Error updating member status:", error);
    }
  };

  const indexOfFirstEntry = totalCount === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const indexOfLastEntry = Math.min(currentPage * entriesPerPage, totalCount);
  const currentEntries = staffs;
  const totalPages = Math.ceil(totalCount / entriesPerPage) || 1;

  const openAssignPondsModal = (staff) => {
    setAssignPondsStaff(staff);
    setAssignPondsMode(isLinkedScientistRow(staff) ? "scientist" : "team");
  };
  const closeAssignPondsModal = () => {
    setAssignPondsStaff(null);
    setAssignPondsMode("scientist");
  };

  const DeleteUser = async (memberId) => {
    const target = staffs.find((s) => String(s.id) === String(memberId));
    if (target && !canDeleteStaffMember(target, userDetails)) {
      showErrorToast("You can't delete your own account.");
      return;
    }

    const result = await Swal.fire({
      title: "Remove this member?",
      text: "They will lose access to this organization's team data.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#14532d",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        await axiosInstance.delete("api/User/DeleteStaff", {
          params: { id: memberId, fisherID },
        });
        showSuccessToast("Member deleted successfully");
        await refetchStaffQuiet();
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
          const errorMessage = error.response?.data?.message || "Failed to delete member";
          showErrorToast(errorMessage);
        }
        console.error("Error deleting member:", error);
      }
    }
  };

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return [...Array(totalPages)].map((_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  }, [totalPages, currentPage]);

  const renderRowActions = (staff, alwaysVisible = false) => {
    if (!canManageTeam) {
      return <span className="donezo-orders-table__date">—</span>;
    }
    return (
      <div className={`donezo-orders-actions${alwaysVisible ? " is-visible" : ""}`}>
        {canAssignPondsFromTeamList(staff) && (
          <button
            type="button"
            className="donezo-orders-actions__btn"
            title="Assign ponds"
            aria-label="Assign ponds"
            onClick={() => openAssignPondsModal(staff)}
          >
            <i className="fa fa-water" aria-hidden />
          </button>
        )}
        <button
          type="button"
          className="donezo-orders-actions__btn"
          title="Edit member"
          aria-label="Edit member"
          onClick={() => history.push(`/UpdateStaff/${staff.id}`)}
        >
          <i className="fa fa-pen" aria-hidden />
        </button>
        {canDeleteStaffMember(staff, userDetails) && (
          <button
            type="button"
            className="donezo-orders-actions__btn donezo-orders-actions__btn--danger"
            title="Delete member"
            aria-label="Delete member"
            onClick={() => DeleteUser(staff.id)}
          >
            <i className="fa fa-trash" aria-hidden />
          </button>
        )}
      </div>
    );
  };

  if (!canOpenTeamPage) {
    return (
      <div className="donezo-page donezo-list-page">
        <div className="container-fluid px-3 px-md-4 py-5">
          <div className="donezo-empty-state mx-auto" style={{ maxWidth: 440 }}>
            <div className="donezo-empty-state__icon" aria-hidden>
              <i className="fa fa-lock" />
            </div>
            <p className="donezo-empty-state__title">Access restricted</p>
            <p className="donezo-empty-state__text">
              Only organization-wide team members can open this page. Contact your organization
              owner if you need access.
            </p>
            <Link to="/" className="btn btn-primary rounded-pill px-4 mt-3">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if ((isCompanyManager || isSuperScoped) && (fisherID == null || fisherID === "")) {
    return (
      <div className="donezo-page donezo-list-page">
        <div className="container-fluid px-3 px-md-4 py-5">
          <div className="donezo-empty-state mx-auto" style={{ maxWidth: 440 }}>
            <div className="donezo-empty-state__icon" aria-hidden>
              <i className="fa fa-building" />
            </div>
            <p className="donezo-empty-state__title">Select an account first</p>
            <p className="donezo-empty-state__text">
              Open a client account from Company Accounts, then manage that organization&apos;s
              team.
            </p>
            <Link to="/company/accounts" className="btn btn-primary rounded-pill px-4 mt-3">
              Company Accounts
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return isLoading ? (
    <Preloader />
  ) : (
    <div className="donezo-page donezo-list-page donezo-orders-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <div className="donezo-orders-toolbar">
          <h1 className="donezo-orders-toolbar__title">Team</h1>

          <label htmlFor="staff-search" className="visually-hidden">
            Search team members
          </label>
          <div className="donezo-orders-toolbar__search">
            <i className="fa fa-search" aria-hidden />
            <input
              id="staff-search"
              type="search"
              className="form-control"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              autoComplete="off"
            />
          </div>

          <div className="donezo-orders-toolbar__actions">
            {canManageTeam && (
              <>
                <Link to="/inviteScientist" className="donezo-orders-toolbar__ghost">
                  <i className="fa fa-envelope" aria-hidden />
                  <span className="d-none d-md-inline">
                    {windowWidth > 576 ? "Invite scientist" : "Invite"}
                  </span>
                </Link>
                <Link to="/createStaff" className="donezo-orders-toolbar__primary">
                  {windowWidth > 576 ? "Create member" : "Add"}
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="donezo-orders-panel">
          <div className="d-none d-md-block table-responsive">
            <table className="table align-middle mb-0 donezo-orders-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="donezo-orders-table__actions-col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {staffs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-0 border-0">
                      <div className="donezo-empty-state">
                        <div className="donezo-empty-state__icon" aria-hidden>
                          <i className="fa fa-users" />
                        </div>
                        <p className="donezo-empty-state__title">No team members found</p>
                        <p className="donezo-empty-state__text">
                          {searchTerm
                            ? "Try a different search or clear the filter."
                            : "Create a member to invite people to this organization."}
                        </p>
                        {canManageTeam && !searchTerm && (
                          <Link
                            to="/createStaff"
                            className="btn btn-primary rounded-pill px-4 mt-3 d-inline-flex align-items-center"
                          >
                            <i className="fa fa-plus me-2" aria-hidden />
                            Create member
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentEntries.map((staff) => (
                    <tr key={staff.id}>
                      <td>
                        <div className="donezo-orders-pond">
                          <span className="donezo-orders-pond__avatar" aria-hidden>
                            {memberInitial(staff.FullName)}
                          </span>
                          <div className="donezo-orders-pond__meta">
                            <span className="donezo-orders-pond__name">{staff.FullName}</span>
                            <span className="donezo-orders-pond__id">#{staff.id}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="donezo-team-email" title={staff.Email || undefined}>
                          {staff.Email || "—"}
                        </span>
                      </td>
                      <td>{staffRoleBadge(staff)}</td>
                      <td>
                        <StaffStatusBadge
                          staff={staff}
                          canManageTeam={canManageTeam}
                          onToggleStatus={updateStatus}
                        />
                      </td>
                      <td className="donezo-orders-table__date">
                        {formatDisplayDate(staff.createdAt)}
                      </td>
                      <td className="donezo-orders-table__actions-col">
                        {renderRowActions(staff)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-md-none donezo-orders-panel__mobile">
            {staffs.length === 0 ? (
              <div className="donezo-empty-state donezo-empty-state--compact">
                <p className="donezo-empty-state__title">No team members</p>
                <p className="donezo-empty-state__text">
                  {searchTerm ? "Try another search." : "No members to show."}
                </p>
                {canManageTeam && !searchTerm && (
                  <Link to="/createStaff" className="btn btn-primary rounded-pill px-4 mt-3">
                    Create member
                  </Link>
                )}
              </div>
            ) : (
              currentEntries.map((staff) => (
                <div key={staff.id} className="donezo-mobile-card">
                  <div className="donezo-mobile-card__head">
                    <div className="donezo-orders-pond">
                      <span className="donezo-orders-pond__avatar" aria-hidden>
                        {memberInitial(staff.FullName)}
                      </span>
                      <div className="donezo-orders-pond__meta">
                        <h2 className="donezo-mobile-card__title mb-0">{staff.FullName}</h2>
                        <span className="donezo-orders-pond__id">#{staff.id}</span>
                      </div>
                    </div>
                    {renderRowActions(staff, true)}
                  </div>
                  <div className="donezo-mobile-card__grid">
                    <div className="donezo-mobile-card__full">
                      <span className="donezo-mobile-card__label">Email</span>
                      <div className="donezo-mobile-card__value donezo-team-email">
                        {staff.Email || "—"}
                      </div>
                    </div>
                    <div>
                      <span className="donezo-mobile-card__label">Role</span>
                      <div className="donezo-mobile-card__value">{staffRoleBadge(staff)}</div>
                    </div>
                    <div>
                      <span className="donezo-mobile-card__label">Status</span>
                      <div className="donezo-mobile-card__value">
                        <StaffStatusBadge
                          staff={staff}
                          canManageTeam={canManageTeam}
                          onToggleStatus={updateStatus}
                        />
                      </div>
                    </div>
                    <div className="donezo-mobile-card__full">
                      <span className="donezo-mobile-card__label">Created</span>
                      <div className="donezo-mobile-card__value">
                        {formatDisplayDate(staff.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {staffs.length > 0 && (
            <div className="donezo-orders-footer">
              <div className="donezo-orders-footer__meta">
                Showing {indexOfFirstEntry}-{indexOfLastEntry} from {totalCount}
              </div>
              <ul className="donezo-orders-pagination">
                <li>
                  <button
                    type="button"
                    className="donezo-orders-pagination__btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    <i className="fa fa-chevron-left" aria-hidden />
                  </button>
                </li>
                {windowWidth < 768 ? (
                  <li>
                    <span className="donezo-orders-pagination__btn is-active">{currentPage}</span>
                  </li>
                ) : (
                  pageNumbers.map((page, idx) => {
                    const prev = pageNumbers[idx - 1];
                    const showEllipsis = prev != null && page - prev > 1;
                    return (
                      <li key={page} className="d-inline-flex align-items-center gap-1">
                        {showEllipsis && (
                          <span className="donezo-orders-pagination__ellipsis">…</span>
                        )}
                        <button
                          type="button"
                          className={`donezo-orders-pagination__btn${
                            page === currentPage ? " is-active" : ""
                          }`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })
                )}
                <li>
                  <button
                    type="button"
                    className="donezo-orders-pagination__btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                  >
                    <i className="fa fa-chevron-right" aria-hidden />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <AssignScientistPondsModal
        isOpen={!!assignPondsStaff}
        onClose={closeAssignPondsModal}
        staffMember={assignPondsStaff}
        fisherID={fisherID}
        assignmentMode={assignPondsMode}
        onAssigned={refetchStaffQuiet}
      />
    </div>
  );
};

export default Staff;
